import { useMemo, useState } from "react";
import {
  COMPETENCY_CATALOG,
  REQUIRED_COMPETENCY_SELECTION,
} from "../../../../utils/competency-catalog";
import DiscStyleTable from "./disc-style-table";

export const COMPETENCY_MODE = {
  PATTERN: "pattern",
  SPECIFIC: "specific",
};

const MODE_LABELS = {
  [COMPETENCY_MODE.PATTERN]: "Competencias según tipos de estilo",
  [COMPETENCY_MODE.SPECIFIC]: "Competencias específicas",
};

/**
 * Descripción de cada modo, a la vista y no en un `title`: un tooltip nativo no
 * aparece al tocar en móvil, tarda ~1s en desplegarse y no es alcanzable por
 * teclado, y esta es la decisión con la que se elige el modo de evaluación.
 *
 * La tarjeta del modo por patrón queda con un párrafo corto y el detalle de qué
 * competencias trae cada estilo vive en el panel de la tabla DISC, que aparece
 * debajo al elegir ese modo.
 */
const MODE_DESCRIPTIONS = {
  [COMPETENCY_MODE.PATTERN]: (
    <p>
      A partir de los resultados de la primera parte del test, se identificarán los{" "}
      <strong>dos estilos de comportamiento predominantes</strong> y se asignarán
      automáticamente las <strong>8 competencias</strong> asociadas a esos estilos.
    </p>
  ),
  [COMPETENCY_MODE.SPECIFIC]: (
    <p>
      Permite personalizar la evaluación mediante la selección manual de 8
      competencias, de acuerdo con las necesidades específicas de desarrollo,
      desempeño o gestión del talento. Debes seleccionar 8 de las siguientes
      competencias.
    </p>
  ),
};

const ALL_GROUPS = "Todos";

/**
 * Selector de modo de evaluación al enviar el Test Growme
 *
 * Controlado por el contenedor: no guarda el modo ni la selección, los reporta.
 * El contenedor decide qué mandar en el payload — en modo por patrón no debe
 * viajar `selectedCompetences`, para que el envío sea idéntico al de hoy.
 *
 * `mode` acepta `null`: es "todavía sin elegir" y entonces no hay tarjeta
 * marcada ni panel debajo. No es un valor de `COMPETENCY_MODE`, justamente porque
 * es la ausencia de modo.
 */
const CompetencyModeSelector = ({
  mode,
  onModeChange,
  selected = [],
  onSelectedChange,
  // El modal de invitación ya lleva este texto en su cabecera y lo pasa en
  // `null` para no repetirlo; los pasos de "crear proceso" insertan el selector
  // inline y no tienen dónde más ponerlo, así que por defecto se muestra.
  heading = "Seleccione el modo de evaluación",
}) => {
  const [activeGroup, setActiveGroup] = useState(ALL_GROUPS);

  // Los grupos salen del catálogo y no de una lista fija: cuando #69 reasigne
  // colores, las pestañas siguen al catálogo sin tocar este componente.
  const groups = useMemo(() => {
    const seen = [];
    COMPETENCY_CATALOG.forEach(({ group }) => {
      if (!seen.includes(group)) seen.push(group);
    });
    return [ALL_GROUPS, ...seen];
  }, []);

  const visible = useMemo(
    () =>
      activeGroup === ALL_GROUPS
        ? COMPETENCY_CATALOG
        : COMPETENCY_CATALOG.filter((c) => c.group === activeGroup),
    [activeGroup]
  );

  const isSelected = (sectionDescription) => selected.includes(sectionDescription);

  const toggle = (sectionDescription) => {
    if (isSelected(sectionDescription)) {
      onSelectedChange(selected.filter((name) => name !== sectionDescription));
      return;
    }
    // El tope se aplica acá y no sólo deshabilitando el submit: llegar a 9 y que
    // el botón se apague sin decir por qué es peor que no poder marcar la novena.
    if (selected.length >= REQUIRED_COMPETENCY_SELECTION) return;
    onSelectedChange([...selected, sectionDescription]);
  };

  return (
    <div className="gm-competency-mode-selector">
      {heading && <span>{heading}</span>}

      <div className="gm-competency-mode-options">
        {[COMPETENCY_MODE.PATTERN, COMPETENCY_MODE.SPECIFIC].map((value) => (
          // La tarjeta entera es el `label`, así que cualquier punto de ella —
          // incluida la descripción — marca el radio. Un `button` envolviendo el
          // input sería HTML inválido y dejaría el radio sin etiqueta asociada.
          <label
            key={value}
            className={`gm-competency-mode-card${mode === value ? " is-selected" : ""}`}
            data-testid={`competency-mode-card-${value}`}
          >
            <span className="gm-competency-mode-card-option">
              <input
                type="radio"
                name="competency-mode"
                value={value}
                checked={mode === value}
                onChange={() => onModeChange(value)}
              />
              <strong>{MODE_LABELS[value]}</strong>
            </span>
            {MODE_DESCRIPTIONS[value]}
          </label>
        ))}
      </div>

      {/* Panel de ancho completo bajo las tarjetas: la tabla y el picker son
          mutuamente excluyentes, y sin modo elegido no se muestra ninguno. */}
      {mode === COMPETENCY_MODE.PATTERN && <DiscStyleTable />}

      {mode === COMPETENCY_MODE.SPECIFIC && (
        <div className="gm-competency-picker">
          <div className="gm-competency-picker-header">
            <div className="gm-competency-picker-tabs">
              {groups.map((group) => (
                <button
                  key={group}
                  type="button"
                  data-testid={`competency-tab-${group}`}
                  className={group === activeGroup ? "is-active" : ""}
                  onClick={() => setActiveGroup(group)}
                >
                  {group}
                </button>
              ))}
            </div>
            <span className="gm-competency-picker-counter">
              {selected.length}/{REQUIRED_COMPETENCY_SELECTION} competencias
            </span>
          </div>

          <ul className="gm-competency-picker-grid">
            {visible.map((competency) => {
              const checked = isSelected(competency.sectionDescription);
              return (
                <li key={competency.key}>
                  <button
                    type="button"
                    // Los nombres no son identificadores únicos en el DOM: hay
                    // competencias que además figuran como "elemento que evalúa"
                    // de otra (p.ej. Proactividad dentro de Determinación).
                    data-testid={`competency-card-${competency.key}`}
                    aria-pressed={checked}
                    className={checked ? "is-selected" : ""}
                    style={{ borderColor: checked ? competency.color : undefined }}
                    onClick={() => toggle(competency.sectionDescription)}
                  >
                    <span className="gm-competency-card-group" style={{ color: competency.color }}>
                      {competency.group}
                    </span>
                    <strong>{competency.label}</strong>
                    <p>{competency.definition}</p>
                    <span className="gm-competency-card-elements-title">
                      ELEMENTOS QUE EVALÚA
                    </span>
                    <ul>
                      {competency.elements.map((element) => (
                        <li key={element}>{element}</li>
                      ))}
                    </ul>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

/**
 * Qué mandar en el payload. En modo por patrón devuelve `undefined` a propósito:
 * la ausencia del campo es lo que el back interpreta como modo por patrón (#63),
 * y así el request queda byte a byte como el de hoy.
 */
export const selectedCompetencesForPayload = (mode, selected) =>
  mode === COMPETENCY_MODE.SPECIFIC ? selected : undefined;

/**
 * El envío exige haber elegido un modo y, en modo específicas, exactamente
 * 8 competencias. Sin modo — `null` o cualquier cosa que no sea un modo conocido —
 * no hay nada válido que enviar.
 */
export const isCompetencySelectionValid = (mode, selected) => {
  if (mode === COMPETENCY_MODE.PATTERN) return true;
  if (mode === COMPETENCY_MODE.SPECIFIC) {
    return selected.length === REQUIRED_COMPETENCY_SELECTION;
  }
  return false;
};

export default CompetencyModeSelector;
